import { z } from "zod";

/**
 * Look and default texts of the PDF documents (quotes, invoices, credit notes, delivery notes,
 * purchase orders), edited from Settings › Documents with a live preview. Stored as one JSON value
 * on the company settings; anything missing falls back on the defaults below.
 */
export const DOCUMENT_FONTS = ["Inter", "Jost"] as const;
export type DocumentFont = (typeof DOCUMENT_FONTS)[number];
export const LOGO_SIZES = ["sm", "md", "lg"] as const;
export type LogoSize = (typeof LOGO_SIZES)[number];
/** Blocks printed under the totals, in the order the administrator chooses. */
export const DOCUMENT_BLOCKS = ["notes", "payment", "terms", "closing", "signature"] as const;
export type DocumentBlock = (typeof DOCUMENT_BLOCKS)[number];
export const SKU_MODES = ["inline", "column", "hidden"] as const;
export type SkuMode = (typeof SKU_MODES)[number];
export const DISCOUNT_MODES = ["auto", "always", "never"] as const;
export type DiscountMode = (typeof DISCOUNT_MODES)[number];
/** Documents drawn in the live preview of Settings › Documents, each with its own settings. */
export const DOCUMENT_KINDS = ["quote", "invoice", "credit_note", "delivery_note", "purchase_order"] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];
/**
 * Default texts are kept per document and per language: the French set, and an English one (`…En`)
 * printed for a customer or supplier who reads English. An English text left empty takes the French
 * one; a credit note text left empty takes the invoice's.
 */
export const DOCUMENT_TEXT_KINDS = [
  "quote",
  "invoice",
  "creditNote",
  "deliveryNote",
  "purchaseOrder",
  "quoteEn",
  "invoiceEn",
  "creditNoteEn",
  "deliveryNoteEn",
  "purchaseOrderEn",
] as const;
export type DocumentTextKind = (typeof DOCUMENT_TEXT_KINDS)[number];
/** The French text sets: the ones a document asks for, its language choosing the set actually printed. */
export type DocumentTextBase = "quote" | "invoice" | "creditNote" | "deliveryNote" | "purchaseOrder";
export const DOCUMENT_LOCALES = ["fr", "en"] as const;
export type DocumentLocaleCode = (typeof DOCUMENT_LOCALES)[number];

const color = z
  .union([z.literal(""), z.string().regex(/^#[0-9a-fA-F]{6}$/, "errors.field.color")])
  .transform((v) => (v === "" ? null : v.toUpperCase()))
  .nullable();

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max, "errors.field.tooLong")
    .transform((v) => (v === "" ? null : v))
    .nullable();

/** Texts of one document kind. Empty = nothing printed (for a credit note: the invoice's text). */
const kindTexts = z.object({
  intro: text(2000),
  /** Printed right under the totals (a word on lead times for quotes, for instance). Absent from older templates. */
  afterTotal: text(2000).default(null),
  terms: text(5000),
  closing: text(500),
  footer: text(2000),
});

const letterheadSchema = z.object({
  showEmail: z.boolean(),
  showPhone: z.boolean(),
  showWebsite: z.boolean(),
  showSiren: z.boolean(),
});
const tableSchema = z.object({
  sku: z.enum(SKU_MODES),
  discount: z.enum(DISCOUNT_MODES),
  vat: z.boolean(),
  description: z.boolean(),
});
const blocksSchema = z
  .array(z.object({ id: z.enum(DOCUMENT_BLOCKS), visible: z.boolean() }))
  .refine(
    (blocks) => blocks.length === DOCUMENT_BLOCKS.length && new Set(blocks.map((b) => b.id)).size === DOCUMENT_BLOCKS.length,
    { message: "errors.field.blocks" },
  );
/** What one document kind sets for itself; null keeps the common setting. */
const kindSettings = z.object({
  letterhead: letterheadSchema.nullable(),
  table: tableSchema.nullable(),
  blocks: blocksSchema.nullable(),
});
export type DocumentKindSettings = z.output<typeof kindSettings>;

export const documentTemplateSchema = z.object({
  colors: z.object({ accent: color, text: color, muted: color }),
  fonts: z.object({ heading: z.enum(DOCUMENT_FONTS), body: z.enum(DOCUMENT_FONTS) }),
  logoSize: z.enum(LOGO_SIZES),
  letterhead: letterheadSchema,
  table: tableSchema,
  blocks: blocksSchema,
  /** Header, table and blocks of each document, when it does not follow the common ones. */
  byKind: z.object({
    quote: kindSettings,
    invoice: kindSettings,
    credit_note: kindSettings,
    delivery_note: kindSettings,
    purchase_order: kindSettings,
  }),
  quote: z.object({ showIban: z.boolean(), signature: z.boolean() }),
  texts: z.object({
    shared: z.object({ footer: text(2000), footerEn: text(2000).default(null) }),
    quote: kindTexts,
    invoice: kindTexts,
    creditNote: kindTexts,
    deliveryNote: kindTexts,
    purchaseOrder: kindTexts,
    quoteEn: kindTexts,
    invoiceEn: kindTexts,
    creditNoteEn: kindTexts,
    deliveryNoteEn: kindTexts,
    purchaseOrderEn: kindTexts,
  }),
});
export type DocumentTemplateInput = z.input<typeof documentTemplateSchema>;
export type DocumentTemplate = z.output<typeof documentTemplateSchema>;

const EMPTY_TEXTS = { intro: null, afterTotal: null, terms: null, closing: null, footer: null };

export const DEFAULT_DOCUMENT_TEMPLATE: DocumentTemplate = {
  colors: { accent: null, text: null, muted: null },
  fonts: { heading: "Jost", body: "Inter" },
  logoSize: "md",
  letterhead: { showEmail: true, showPhone: true, showWebsite: false, showSiren: false },
  table: { sku: "inline", discount: "auto", vat: true, description: true },
  blocks: DOCUMENT_BLOCKS.map((id) => ({ id, visible: true })),
  byKind: {
    quote: { letterhead: null, table: null, blocks: null },
    invoice: { letterhead: null, table: null, blocks: null },
    credit_note: { letterhead: null, table: null, blocks: null },
    delivery_note: { letterhead: null, table: null, blocks: null },
    purchase_order: { letterhead: null, table: null, blocks: null },
  },
  quote: { showIban: false, signature: true },
  texts: {
    shared: { footer: null, footerEn: null },
    quote: EMPTY_TEXTS,
    invoice: EMPTY_TEXTS,
    creditNote: EMPTY_TEXTS,
    deliveryNote: EMPTY_TEXTS,
    purchaseOrder: EMPTY_TEXTS,
    quoteEn: EMPTY_TEXTS,
    invoiceEn: EMPTY_TEXTS,
    creditNoteEn: EMPTY_TEXTS,
    deliveryNoteEn: EMPTY_TEXTS,
    purchaseOrderEn: EMPTY_TEXTS,
  },
};

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

function merge(defaults: unknown, stored: unknown): unknown {
  if (isRecord(defaults)) {
    if (!isRecord(stored)) return defaults;
    return Object.fromEntries(
      Object.entries(defaults).map(([key, value]) => [key, key in stored ? merge(value, stored[key]) : value]),
    );
  }
  return stored === undefined ? defaults : stored;
}

/**
 * The stored value completed with the defaults, then validated: a template saved before an option
 * existed still yields a full one, and an invalid stored value falls back on the defaults entirely.
 */
export function resolveTemplate(stored: unknown): DocumentTemplate {
  const parsed = documentTemplateSchema.safeParse(merge(DEFAULT_DOCUMENT_TEMPLATE, stored));
  return parsed.success ? parsed.data : DEFAULT_DOCUMENT_TEMPLATE;
}

type KindTexts = DocumentTemplate["texts"]["quote"];
const TEXT_FIELDS = ["intro", "afterTotal", "terms", "closing", "footer"] as const;

/** Field by field, the first set that has a text. */
function firstOf(...sets: KindTexts[]): KindTexts {
  return Object.fromEntries(TEXT_FIELDS.map((f) => [f, sets.find((s) => s[f] !== null)?.[f] ?? null])) as KindTexts;
}

/**
 * Texts a document prints in its reader's language. An English document takes the English text,
 * else the French one; a credit note takes its own text, else the invoice's (English first).
 */
export function documentTexts(template: DocumentTemplate, kind: DocumentTextBase, locale: DocumentLocaleCode = "fr"): KindTexts {
  const t = template.texts;
  const own = locale === "en" ? [t[`${kind}En`], t[kind]] : [t[kind]];
  const invoice = locale === "en" ? [t.invoiceEn, t.invoice] : [t.invoice];
  return kind === "creditNote"
    ? firstOf(...own.slice(0, 1), ...invoice.slice(0, 1), ...own.slice(1), ...invoice.slice(1))
    : firstOf(...own);
}

/** The footer every document shares, in its reader's language (the French one when no English is written). */
export function sharedFooter(template: DocumentTemplate, locale: DocumentLocaleCode = "fr"): string | null {
  return (locale === "en" ? template.texts.shared.footerEn : null) ?? template.texts.shared.footer;
}

/** The template as one document kind prints it: its own header, table and blocks where it has them. */
export function templateForKind(template: DocumentTemplate, kind: DocumentKind): DocumentTemplate {
  const own = template.byKind[kind];
  return {
    ...template,
    letterhead: own.letterhead ?? template.letterhead,
    table: own.table ?? template.table,
    blocks: own.blocks ?? template.blocks,
  };
}

export const documentPreviewSchema = z.object({
  kind: z.enum(DOCUMENT_KINDS),
  /** Documents are printed in their reader's language (the supplier's for a purchase order). */
  locale: z.enum(DOCUMENT_LOCALES).default("fr"),
  template: documentTemplateSchema,
});
export type DocumentPreviewInput = z.input<typeof documentPreviewSchema>;
